import produce from 'immer';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ResultsRowMetadata, ResultsTableMetadata} from 'notebook/integrations/results/ResultsTableConstants';
import {ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {ACTION_SUBMIT_RESULTS} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonStyle,
  getSubmitButtonLabel,
  getSubmitButtonHidden,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  isBatchingRequired,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {StructuredTableSubmissionScope} from '../StructuredTableApiConstants';
import {StructuredTableKind, TableActionFunctionParameters} from '../StructuredTableConstants';
import {
  getRowAwareLoadingMessage,
  RowSubmissionRange,
  getStructuredTableSubmissionScope,
} from './makeSubmitRowActionFunction';
import {checkStructuredTableValidationErrors} from './validateStructuredTableActionFunction';

async function attemptCreateResults(
  entryId: string,
  structuredTable: ResultsTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  submissionScope: StructuredTableSubmissionScope,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ActionFunctionDAO.ResultSubmitResponse> {
  return await ActionFunctionDAO.submitResults(
    entryId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    submissionScope,
    latestVersionId,
    dataModsHeader
  );
}

async function attemptBatchedCreateResults(
  entryId: string,
  batchSize: number,
  structuredTable: ResultsTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection: RowSubmissionRange | undefined,
  submissionScope: StructuredTableSubmissionScope,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ResultSubmitResponse>> {
  let responseData: ActionFunctionDAO.ResultSubmitResponse = {sampleIds: [], entryVersionId: -1};
  let latestVersionId: number | undefined;
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  let partialSuccess = false;
  const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);

  try {
    for (const {range, evaluatedRowBatch} of batches) {
      if (updateStatus) {
        updateStatus(
          getRowAwareLoadingMessage(range, 'Updating Results For Rows', 'Updating Results For Row')
        );
      }
      const batchResult = await attemptCreateResults(
        entryId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        submissionScope,
        range,
        latestVersionId,
        dataModsHeader
      );

      latestVersionId = batchResult.entryVersionId;
      submittedRange = {...submittedRange, end: range.end};
      partialSuccess = true;
      responseData = produce(responseData, (draft) => {
        if (draft.sampleIds == null) {
          draft.sampleIds = [];
        }
        draft.sampleIds.push(...(batchResult.sampleIds ?? []));
        draft.entryVersionId = batchResult.entryVersionId;
      });
    }
  } catch (e) {
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }
    throw e;
  }

  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function doPerformCreateResults(
  entryId: string,
  structuredTable: ResultsTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ResultSubmitResponse>> {
  const submissionScope = getStructuredTableSubmissionScope(selection);
  const batchSize = getSubmissionBatchSize(StructuredTableKind.RESULTS);
  if (isBatchingRequired(computedTable, batchSize, selection)) {
    return await attemptBatchedCreateResults(
      entryId,
      batchSize!,
      structuredTable,
      computedTable,
      actionFunctionContext,
      selection,
      submissionScope,
      updateStatus,
      dataModsHeader
    );
  } else {
    const pertinentComputedTable = selection
      ? computedTable.slice(selection.start, selection.end)
      : computedTable;
    const responseData = await attemptCreateResults(
      entryId,
      structuredTable,
      pertinentComputedTable,
      actionFunctionContext,
      submissionScope,
      selection,
      undefined, // latestVersionId
      dataModsHeader
    );
    return {resultType: ActionResultType.SUCCESS, responseData};
  }
}

export async function onInvokeCreateResultsActionFunction({
  entryOrTemplateId,
  structuredTable,
  structuredTableAdapter,
  computedTable,
  actionFunctionContext,
  table,
}: TableActionFunctionParameters<ResultsTableMetadata, ResultsRowMetadata>): Promise<void> {
  assert(
    entryOrTemplateId != null && APIUtil.isBaseEntry(entryOrTemplateId),
    'Create results action is only supported in entries.',
    {apiId: entryOrTemplateId}
  );

  await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.ResultSubmitResponse>({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Updating Results...',
    autoSaverAction: ACTION_SUBMIT_RESULTS,
    doValidationAction: async () => {
      await checkStructuredTableValidationErrors(
        entryOrTemplateId,
        structuredTable,
        computedTable,
        actionFunctionContext
      );
    },
    doPerformAction: ({updateStatus, dataModsHeader}) =>
      doPerformCreateResults(
        entryOrTemplateId,
        structuredTable as ResultsTable,
        computedTable,
        actionFunctionContext,
        undefined, // selection
        updateStatus,
        dataModsHeader
      ),
    doPostRefresh: (actionResult) => {
      invokeStructuredTableMappings(
        entryOrTemplateId,
        structuredTable,
        // on a partial success we want to map only the rows that were successfully submitted
        actionResult.resultType === ActionResultType.PARTIAL_SUCCESS ? actionResult.submittedRange : undefined
      );
      return Promise.resolve();
    },
    structuredTables: [structuredTable],
    structuredTableAdapters: [structuredTableAdapter],
    computedTables: [computedTable],
    tables: table ? [table] : [],
  });
}

export const submitResultsActionFunction = makeSimpleActionFunction<ResultsTableMetadata, {}>({
  label: (params) => getSubmitButtonLabel(params.tableStatus),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params.tableStatus, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeCreateResultsActionFunction,
  getDisabled: isTableActionButtonDisabled,
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
